import React, { useState, useEffect } from 'react';
import { useAppDispatch } from '../../hooks/redux';
import { showToast } from '../../features/ui/uiSlice';
import api from '../../utils/axiosConfig';
import {
    ArrowUpTrayIcon,
    CheckCircleIcon,
    XCircleIcon,
    ClockIcon,
    EyeIcon,
    UserIcon,
    CalendarIcon,
    CurrencyDollarIcon,
    BuildingLibraryIcon
} from '@heroicons/react/24/outline';

interface WithdrawalRequest {
    _id: string;
    amount: number;
    status: 'pending' | 'approved' | 'processed' | 'rejected';
    requestedAt: string;
    processedAt?: string;
    bankDetails: {
        accountNumber: string;
        bankName: string;
        ifscCode: string;
        accountHolderName: string;
    };
    user: {
        _id: string;
        firstName: string;
        lastName: string;
        email: string;
        currentBalance?: number;
        verifiedBalance?: number;
        pendingBalance?: number;
    };
    processedBy?: {
        _id: string;
        firstName: string;
        lastName: string;
    };
    notes?: string;
}

const AdminWithdrawalManagement: React.FC = () => {
    const dispatch = useAppDispatch();
    const [withdrawals, setWithdrawals] = useState<WithdrawalRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedWithdrawal, setSelectedWithdrawal] = useState<WithdrawalRequest | null>(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [processing, setProcessing] = useState<string | null>(null);
    const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'processed' | 'rejected'>('all');

    const fetchWithdrawals = async () => {
        try {
            setLoading(true);
            const response = await api.get('/wallets/admin/withdrawal-requests');
            if (response.data.success) {
                // Handle the correct API response structure
                const responseData = response.data.data || {};
                const withdrawalsData = responseData.withdrawalRequests || [];
                setWithdrawals(Array.isArray(withdrawalsData) ? withdrawalsData : []);
            } else {
                setWithdrawals([]);
            }
        } catch (error: any) {
            console.error('Error fetching withdrawals:', error);
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to fetch withdrawal requests',
                type: 'error'
            }));
            setWithdrawals([]); // Set empty array on error
        } finally {
            setLoading(false);
        }
    };

    const processWithdrawal = async (requestId: string, action: 'approve' | 'reject', notes?: string) => {
        try {
            setProcessing(requestId);
            const requestData = {
                status: action === 'approve' ? 'approved' : 'rejected',
                notes: notes || ''
            };
            console.log('Sending withdrawal request:', requestData);
            const response = await api.put(`/wallets/admin/withdrawal-requests/${requestId}`, requestData);
            if (response.data.success) {
                dispatch(showToast({
                    message: `Withdrawal request ${action}d successfully`,
                    type: 'success'
                }));
                await fetchWithdrawals();
                setShowDetailsModal(false);
            }
        } catch (error: any) {
            console.error('Error processing withdrawal:', error);
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to process withdrawal request',
                type: 'error'
            }));
        } finally {
            setProcessing(null);
        }
    };

    const openDetailsModal = (withdrawal: WithdrawalRequest) => {
        setSelectedWithdrawal(withdrawal);
        setShowDetailsModal(true);
    };

    useEffect(() => {
        fetchWithdrawals();
    }, []);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex justify-center items-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400 text-lg">Loading withdrawal requests...</p>
                </div>
            </div>
        );
    }

    // Ensure withdrawals is always an array
    const withdrawalsArray = Array.isArray(withdrawals) ? withdrawals : [];

    const filteredWithdrawals = filter === 'all' ? withdrawalsArray : withdrawalsArray.filter(w => w.status === filter);
    const pendingCount = withdrawalsArray.filter(w => w.status === 'pending').length;
    const approvedCount = withdrawalsArray.filter(w => w.status === 'approved').length;
    const processedCount = withdrawalsArray.filter(w => w.status === 'processed').length;
    const rejectedCount = withdrawalsArray.filter(w => w.status === 'rejected').length;

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
            case 'approved': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
            case 'processed': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
            case 'rejected': return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
            default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'pending': return <ClockIcon className="h-4 w-4" />;
            case 'approved': return <CheckCircleIcon className="h-4 w-4" />;
            case 'processed': return <CheckCircleIcon className="h-4 w-4" />;
            case 'rejected': return <XCircleIcon className="h-4 w-4" />;
            default: return <ClockIcon className="h-4 w-4" />;
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <div className="container mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-xl">
                            <ArrowUpTrayIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Withdrawal Management</h1>
                            <p className="text-gray-600 dark:text-gray-400">Manage and process user withdrawal requests</p>
                        </div>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Requests</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">{withdrawalsArray.length}</p>
                            </div>
                            <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                                <ArrowUpTrayIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Pending</p>
                                <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{pendingCount}</p>
                            </div>
                            <div className="p-3 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg">
                                <ClockIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Processed</p>
                                <p className="text-2xl font-bold text-green-600 dark:text-green-400">{processedCount}</p>
                            </div>
                            <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-lg">
                                <CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Rejected</p>
                                <p className="text-2xl font-bold text-red-600 dark:text-red-400">{rejectedCount}</p>
                            </div>
                            <div className="p-3 bg-red-100 dark:bg-red-900/20 rounded-lg">
                                <XCircleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Balance Summary */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 mb-8 hidden">
                    <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
                                <CurrencyDollarIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Withdrawal Balance Summary</h2>
                                <p className="text-sm text-gray-600 dark:text-gray-400">System-wide withdrawal balance overview</p>
                            </div>
                        </div>
                    </div>
                    <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                            <div className="text-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                <div className="flex items-center justify-center mb-2">
                                    <div className="p-2 bg-green-100 dark:bg-green-800/30 rounded-lg">
                                        <CurrencyDollarIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                                    </div>
                                </div>
                                <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Total Available</h3>
                                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                                    ₹{withdrawalsArray.reduce((sum, w) => sum + (w.user?.currentBalance || 0), 0).toLocaleString()}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">All user balances</p>
                            </div>

                            <div className="text-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                <div className="flex items-center justify-center mb-2">
                                    <div className="p-2 bg-yellow-100 dark:bg-yellow-800/30 rounded-lg">
                                        <ClockIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
                                    </div>
                                </div>
                                <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Pending Withdrawals</h3>
                                <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                                    ₹{withdrawalsArray.filter(w => w.status === 'pending').reduce((sum, w) => sum + w.amount, 0).toLocaleString()}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">Awaiting processing</p>
                            </div>

                            <div className="text-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                <div className="flex items-center justify-center mb-2">
                                    <div className="p-2 bg-blue-100 dark:bg-blue-800/30 rounded-lg">
                                        <CheckCircleIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                                    </div>
                                </div>
                                <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Approved Withdrawals</h3>
                                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                                    ₹{withdrawalsArray.filter(w => w.status === 'approved').reduce((sum, w) => sum + w.amount, 0).toLocaleString()}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">Approved but not processed</p>
                            </div>

                            <div className="text-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                <div className="flex items-center justify-center mb-2">
                                    <div className="p-2 bg-green-100 dark:bg-green-800/30 rounded-lg">
                                        <CurrencyDollarIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                                    </div>
                                </div>
                                <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Available for New Withdrawals</h3>
                                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                                    ₹{(withdrawalsArray.reduce((sum, w) => sum + (w.user?.currentBalance || 0), 0) -
                                        withdrawalsArray.filter(w => w.status === 'pending' || w.status === 'approved').reduce((sum, w) => sum + w.amount, 0)).toLocaleString()}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">After pending & approved</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filter Tabs */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 mb-8">
                    <div className="p-4">
                        <div className="flex space-x-1">
                            {[
                                { id: 'all', label: 'All', count: withdrawalsArray.length },
                                { id: 'pending', label: 'Pending', count: pendingCount },
                                { id: 'approved', label: 'Approved', count: approvedCount },
                                { id: 'processed', label: 'Processed', count: processedCount },
                                { id: 'rejected', label: 'Rejected', count: rejectedCount },
                            ].map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setFilter(tab.id as any)}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-sm transition-all duration-200 ${filter === tab.id
                                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 shadow-sm'
                                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700'
                                        }`}
                                >
                                    {tab.label}
                                    <span className={`px-2 py-1 rounded-full text-xs ${filter === tab.id
                                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                                        }`}>
                                        {tab.count}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Withdrawal Requests List */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                                <ArrowUpTrayIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Withdrawal Requests</h2>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Review and process withdrawal requests</p>
                            </div>
                        </div>
                    </div>
                    <div className="p-6">
                        {filteredWithdrawals.length > 0 ? (
                            <div className="space-y-4">
                                {filteredWithdrawals.map((withdrawal) => (
                                    <div key={withdrawal._id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                                        <div className="flex items-center gap-4">
                                            <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                                                <UserIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-gray-900 dark:text-white">
                                                    {withdrawal.user.firstName} {withdrawal.user.lastName}
                                                </h3>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">{withdrawal.user.email}</p>
                                                <div className="flex items-center gap-4 mt-1">
                                                    <p className="text-xs text-gray-500 dark:text-gray-500">
                                                        Requested: {new Date(withdrawal.requestedAt).toLocaleDateString()}
                                                    </p>
                                                    <p className="text-xs text-green-600 dark:text-green-400 font-medium">
                                                        Amount: ₹{withdrawal.amount.toLocaleString()}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(withdrawal.status)}`}>
                                                {getStatusIcon(withdrawal.status)}
                                                {withdrawal.status}
                                            </span>
                                            <button
                                                onClick={() => openDetailsModal(withdrawal)}
                                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
                                            >
                                                <EyeIcon className="h-4 w-4" />
                                                View Details
                                            </button>
                                            {withdrawal.status === 'pending' && (
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => processWithdrawal(withdrawal._id, 'approve')}
                                                        disabled={processing === withdrawal._id}
                                                        className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors disabled:opacity-50"
                                                    >
                                                        {processing === withdrawal._id ? (
                                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                        ) : (
                                                            <CheckCircleIcon className="h-4 w-4" />
                                                        )}
                                                        Approve
                                                    </button>
                                                    <button
                                                        onClick={() => processWithdrawal(withdrawal._id, 'reject')}
                                                        disabled={processing === withdrawal._id}
                                                        className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50"
                                                    >
                                                        {processing === withdrawal._id ? (
                                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                        ) : (
                                                            <XCircleIcon className="h-4 w-4" />
                                                        )}
                                                        Reject
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                                    <ArrowUpTrayIcon className="h-8 w-8 text-gray-400" />
                                </div>
                                <p className="text-gray-600 dark:text-gray-400 text-lg font-medium">No withdrawal requests found</p>
                                <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">No requests match the current filter</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Withdrawal Details Modal */}
                {showDetailsModal && selectedWithdrawal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    Withdrawal Request Details
                                </h3>
                                <button
                                    onClick={() => setShowDetailsModal(false)}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                >
                                    <XCircleIcon className="h-6 w-6" />
                                </button>
                            </div>

                            <div className="space-y-6">
                                {/* User Information */}
                                <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-xl">
                                    <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                        <UserIcon className="h-5 w-5" />
                                        User Information
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
                                            <p className="text-sm text-gray-900 dark:text-white">{selectedWithdrawal.user.firstName} {selectedWithdrawal.user.lastName}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
                                            <p className="text-sm text-gray-900 dark:text-white">{selectedWithdrawal.user.email}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Withdrawal Details */}
                                <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-xl">
                                    <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                        <CurrencyDollarIcon className="h-5 w-5" />
                                        Withdrawal Details
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Amount</label>
                                            <p className="text-sm text-gray-900 dark:text-white font-semibold">₹{selectedWithdrawal.amount.toLocaleString()}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                                            <span className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedWithdrawal.status)}`}>
                                                {getStatusIcon(selectedWithdrawal.status)}
                                                {selectedWithdrawal.status}
                                            </span>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Requested Date</label>
                                            <p className="text-sm text-gray-900 dark:text-white">{new Date(selectedWithdrawal.requestedAt).toLocaleDateString()}</p>
                                        </div>
                                        {selectedWithdrawal.processedAt && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Processed Date</label>
                                                <p className="text-sm text-gray-900 dark:text-white">{new Date(selectedWithdrawal.processedAt).toLocaleDateString()}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Bank Details */}
                                <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-xl">
                                    <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                        <BuildingLibraryIcon className="h-5 w-5" />
                                        Bank Details
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Bank Name</label>
                                            <p className="text-sm text-gray-900 dark:text-white">{selectedWithdrawal.bankDetails.bankName}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Account Number</label>
                                            <p className="text-sm text-gray-900 dark:text-white font-mono">{selectedWithdrawal.bankDetails.accountNumber}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">IFSC Code</label>
                                            <p className="text-sm text-gray-900 dark:text-white font-mono">{selectedWithdrawal.bankDetails.ifscCode}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Account Holder</label>
                                            <p className="text-sm text-gray-900 dark:text-white">{selectedWithdrawal.bankDetails.accountHolderName}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                {selectedWithdrawal.status === 'pending' && (
                                    <div className="flex justify-end gap-3 pt-4">
                                        <button
                                            onClick={() => processWithdrawal(selectedWithdrawal._id, 'reject')}
                                            disabled={processing === selectedWithdrawal._id}
                                            className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50"
                                        >
                                            {processing === selectedWithdrawal._id ? (
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            ) : (
                                                <XCircleIcon className="h-4 w-4" />
                                            )}
                                            Reject Request
                                        </button>
                                        <button
                                            onClick={() => processWithdrawal(selectedWithdrawal._id, 'approve')}
                                            disabled={processing === selectedWithdrawal._id}
                                            className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors disabled:opacity-50"
                                        >
                                            {processing === selectedWithdrawal._id ? (
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            ) : (
                                                <CheckCircleIcon className="h-4 w-4" />
                                            )}
                                            Approve & Process
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminWithdrawalManagement;
